import React, { useState } from 'react';
import { X, MessageSquare, Star } from 'lucide-react';

const AddReviewModal = ({ isOpen, onClose, submission, onReviewAdded }) => {
  const [formData, setFormData] = useState({
    summary: '',
    strengths: '',
    weaknesses: '',
    otherComments: '',
    rating: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const MAX_WORD_COUNT = 10000;

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.summary.trim()) {
      setError('Summary is required.');
      return;
    }
    if (!formData.strengths.trim()) {
      setError('Strengths is required.');
      return;
    }
    if (!formData.weaknesses.trim()) {
      setError('Weaknesses is required.');
      return;
    }
    const ratingValue = Number.parseFloat(formData.rating);
    if (formData.rating === '' || Number.isNaN(ratingValue) || ratingValue < 0 || ratingValue > 10) {
      setError('Rating (0-10) is required.');
      return;
    }
    const normalizedRating = Math.round(ratingValue * 10) / 10;

    // Validate word count limits
    if (getWordCount(formData.summary) > MAX_WORD_COUNT) {
      setError(`Summary exceeds ${MAX_WORD_COUNT} words limit.`);
      return;
    }
    if (getWordCount(formData.strengths) > MAX_WORD_COUNT) {
      setError(`Strengths exceeds ${MAX_WORD_COUNT} words limit.`);
      return;
    }
    if (getWordCount(formData.weaknesses) > MAX_WORD_COUNT) {
      setError(`Weaknesses exceeds ${MAX_WORD_COUNT} words limit.`);
      return;
    }
    if (formData.otherComments && getWordCount(formData.otherComments) > MAX_WORD_COUNT) {
      setError(`Other Comments exceeds ${MAX_WORD_COUNT} words limit.`);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const toList = (value) =>
        value
          .split(/\n+/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0);

      // Assemble structured review content with explicit sections
      const reviewContent = {
        Summary: formData.summary.trim(),
        Strengths: toList(formData.strengths),
        Weaknesses: toList(formData.weaknesses),
        'Other Comments': formData.otherComments.trim(),
        Rating: normalizedRating
      };

      // Prepare the API payload (matching the expected server format)
      const reviewPayload = {
        code: 200,
        aixiv_id: submission.id,
        version: submission.currentVersion,
        doc_type: submission.type,
        review_results: reviewContent,
        reviewer: 'human'
      };
      console.log('Submitting review with payload:', reviewPayload);
      // Submit the review
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/submit-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reviewPayload)
      });

      const responseText = await response.text();
      console.log('Submit review response:', response.status, responseText);

      if (!response.ok) {
        throw new Error(`Failed to submit review: ${response.status} - ${responseText}`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse submit response as JSON:', parseError);
        throw new Error('Invalid response format from server');
      }

      console.log('Submit review result:', result);

      if (result.code === 200) {
        onReviewAdded();
        setTimeout(() => {
          onClose();
          setFormData({
            summary: '',
            strengths: '',
            weaknesses: '',
            otherComments: '',
            rating: ''
          });
        }, 2000);
      } else {
        throw new Error('Failed to submit review');
      }

    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ summary: '', strengths: '', weaknesses: '', otherComments: '', rating: '' });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Add Review
          </h2>
          <button
            onClick={handleCancel}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Summary Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <MessageSquare className="h-4 w-4 mr-2" />
                Summary <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Provide a brief summary of the paper..."
                rows={4}
                className="input-field resize-none"
                required
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getWordCount(formData.summary)} / {MAX_WORD_COUNT} words
              </div>
            </div>

            {/* Strengths Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <MessageSquare className="h-4 w-4 mr-2" />
                Strengths <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={formData.strengths}
                onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                placeholder="What are the main strengths of this work?"
                rows={4}
                className="input-field resize-none"
                required
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getWordCount(formData.strengths)} / {MAX_WORD_COUNT} words
              </div>
            </div>

            {/* Weaknesses Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <MessageSquare className="h-4 w-4 mr-2" />
                Weaknesses <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={formData.weaknesses}
                onChange={(e) => setFormData({ ...formData, weaknesses: e.target.value })}
                placeholder="What are the main weaknesses or limitations?"
                rows={4}
                className="input-field resize-none"
                required
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getWordCount(formData.weaknesses)} / {MAX_WORD_COUNT} words
              </div>
            </div>

            {/* Other Comments Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <MessageSquare className="h-4 w-4 mr-2" />
                Other Comments
              </label>
              <textarea
                value={formData.otherComments}
                onChange={(e) => setFormData({ ...formData, otherComments: e.target.value })}
                placeholder="Additional comments or suggestions (optional)..."
                rows={3}
                className="input-field resize-none"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getWordCount(formData.otherComments)} / {MAX_WORD_COUNT} words
              </div>
            </div>

            {/* Rating Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Star className="h-4 w-4 mr-2" />
                Rating (0-10) <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                placeholder="Enter a rating from 0.0 to 10.0"
                className="input-field"
                required
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Rate the overall quality of the submission (0.0 = lowest, 10.0 = highest)
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center space-x-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Add Review</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddReviewModal; 